// Notificaciones push del lado del navegador: registro del service worker,
// permiso y suscripcion.
//
// Ojo con el iPhone: Apple solo habilita Web Push si la web esta AGREGADA A LA
// PANTALLA DE INICIO (iOS 16.4 o mayor). Hasta que el usuario haga eso,
// `PushManager` directamente no existe. Por eso el sistema nunca depende del
// push: el contador de avisos dentro de la app funciona en todos lados.

import { api } from "./api";
import { iconoCampana } from "./iconos";
import type { AvisosRespuesta } from "../lib/types";

export type EstadoAvisos =
  | "activados"
  | "desactivados"
  | "bloqueados" // el usuario dijo que no; hay que habilitarlo desde el navegador
  | "instalar" // iPhone sin la app en la pantalla de inicio
  | "no-soportado" // navegador viejo
  | "sin-claves"; // la instalacion no tiene claves VAPID configuradas

function bytesDeBase64url(s: string): ArrayBuffer {
  const relleno = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + relleno).replace(/-/g, "+").replace(/_/g, "/"));
  const buf = new ArrayBuffer(bin.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return buf;
}

function base64urlDe(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  let bin = "";
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** ¿La app se esta viendo instalada (no en una pestana del navegador)? */
export function estaInstalada(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function esIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

let registro: ServiceWorkerRegistration | null = null;

/** Registra el service worker. Sin el no hay push ni instalacion. */
export async function registrarServiceWorker(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    registro = await navigator.serviceWorker.register("/sw.js");
  } catch (e) {
    console.warn("No se pudo registrar el service worker:", e);
  }
}

async function claveDelServidor(): Promise<string | null> {
  try {
    const r = await api.get<{ clave: string | null }>("/api/push/clave");
    return r.clave;
  } catch {
    return null;
  }
}

export async function estadoAvisos(): Promise<EstadoAvisos> {
  if (!("Notification" in window)) return "no-soportado";
  if (!("PushManager" in window)) {
    // En iPhone esto solo pasa cuando NO esta agregada a la pantalla de inicio.
    return esIOS() && !estaInstalada() ? "instalar" : "no-soportado";
  }
  if (!(await claveDelServidor())) return "sin-claves";
  if (Notification.permission === "denied") return "bloqueados";

  const reg = registro ?? (await navigator.serviceWorker.getRegistration());
  const sub = await reg?.pushManager.getSubscription();
  return sub ? "activados" : "desactivados";
}

/**
 * Pide el permiso y deja la suscripcion guardada en el servidor.
 * Se tiene que llamar DESDE UN CLIC del usuario: los navegadores ignoran el
 * pedido de permiso si no viene de un gesto suyo.
 */
export async function activarAvisos(): Promise<EstadoAvisos> {
  const clave = await claveDelServidor();
  if (!clave) return "sin-claves";

  const permiso = await Notification.requestPermission();
  if (permiso !== "granted") return permiso === "denied" ? "bloqueados" : "desactivados";

  const reg = registro ?? (await navigator.serviceWorker.ready);
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: bytesDeBase64url(clave),
    }));

  await api.post("/api/push/suscribir", {
    endpoint: sub.endpoint,
    p256dh: base64urlDe(sub.getKey("p256dh")),
    auth: base64urlDe(sub.getKey("auth")),
  });
  return "activados";
}

export async function desactivarAvisos(): Promise<EstadoAvisos> {
  const reg = registro ?? (await navigator.serviceWorker.getRegistration());
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.del(`/api/push/suscribir?endpoint=${encodeURIComponent(sub.endpoint)}`);
    await sub.unsubscribe();
  }
  return "desactivados";
}

/**
 * Contador de avisos sin leer de la barra.
 *
 * Vive aca y no en main.ts para que la pantalla de avisos pueda apagar el
 * numero apenas marca todo como leido, sin que main.ts importe la pagina y la
 * pagina importe a main.ts (import circular).
 *
 * Funciona siempre, aunque el usuario no haya habilitado las notificaciones
 * del celular o este en un iPhone sin la app en la pantalla de inicio.
 */
export async function refrescarContador(): Promise<void> {
  const chip = document.getElementById("avisosNumero");
  if (!chip) return;
  try {
    const r = await api.get<AvisosRespuesta>("/api/notificaciones");
    chip.textContent = r.pendientes > 9 ? "9+" : String(r.pendientes);
    chip.classList.toggle("hidden", r.pendientes === 0);
  } catch {
    /* sin conexion: se reintenta en el proximo ciclo */
  }
}

/**
 * Evento interno que se dispara cuando llega un push con la app abierta.
 *
 * El service worker no puede tocar el DOM: manda un `postMessage` y aca se
 * traduce a un evento del `window`, que cada pantalla escucha para recargar lo
 * suyo. Asi el service worker no sabe nada de las pantallas.
 */
export const EVENTO_AVISO = "pediconfi:aviso";

/** Puente service worker -> app. Se engancha una sola vez, al iniciar. */
export function escucharPush(): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (ev: MessageEvent) => {
    if ((ev.data as { tipo?: string } | null)?.tipo !== "aviso") return;
    void refrescarContador();
    window.dispatchEvent(new CustomEvent(EVENTO_AVISO));
  });
}

/**
 * Corre `accion` cuando llega un aviso por push, mientras `ancla` siga en
 * pantalla.
 *
 * El router reemplaza el innerHTML sin avisarle a nadie, asi que el listener se
 * desengancha solo al notar que su ancla ya no esta en el documento: si no,
 * cada visita a la pantalla dejaria uno vivo para siempre.
 */
export function alLlegarAviso(ancla: Element, accion: () => void): void {
  const fn = (): void => {
    if (!document.body.contains(ancla)) {
      window.removeEventListener(EVENTO_AVISO, fn);
      return;
    }
    accion();
  };
  window.addEventListener(EVENTO_AVISO, fn);
}

/**
 * Color del estado de las notificaciones del celular: verde si estan llegando,
 * rojo si no.
 *
 * Se mira de reojo desde el mostrador, asi que tiene que cantar sin leerlo. El
 * fondo va translucido (`/50`) para que no tape la pantalla, con borde y punto
 * del mismo color: en un celular al sol un fondo palido solo no se distingue.
 *
 * Todo lo que no sea "activados" es rojo, aunque el motivo cambie (bloqueados,
 * iPhone sin instalar, sin claves): para el que mira, el hecho es el mismo —no
 * le van a llegar los avisos—. El motivo lo explica el texto de abajo.
 */
export function estiloAvisos(estado: EstadoAvisos): { caja: string; punto: string } {
  return estado === "activados"
    ? {
        caja: "border-green-300 bg-green-50/50 text-green-900",
        punto: "bg-green-500",
      }
    : {
        caja: "border-red-300 bg-red-50/50 text-red-900",
        punto: "bg-red-500",
      };
}

/**
 * ¿Se esta usando desde un celular o una tablet? Solo para elegir la palabra
 * ("del celular" / "de esta computadora"): no decide nada de lo que se puede
 * hacer. `pointer: coarse` = el puntero principal es un dedo.
 */
function esTactil(): boolean {
  return window.matchMedia("(pointer: coarse)").matches;
}

/**
 * Texto para el usuario segun el estado.
 *
 * Siempre nombra el APARATO ("del celular", "de esta computadora"): este boton
 * es el PERMISO del navegador, otra cosa que la campana de la barra, que
 * muestra los mismos avisos dentro de la app y funciona con el permiso o sin
 * el. Y el permiso es por aparato: activarlo en el celular no lo activa en la
 * computadora.
 */
export function textoAvisos(estado: EstadoAvisos): { titulo: string; detalle: string } {
  const tactil = esTactil();
  const de = tactil ? "del celular" : "de esta computadora";
  switch (estado) {
    case "activados":
      return {
        titulo: `Notificaciones ${de} activadas`,
        detalle: tactil
          ? "Te llegan al celular aunque la app este cerrada."
          : "Te saltan en esta computadora mientras el navegador este abierto.",
      };
    case "desactivados":
      return {
        titulo: `Activar notificaciones ${de}`,
        detalle: "Para enterarte al toque de los pedidos nuevos.",
      };
    case "bloqueados":
      return {
        titulo: `Notificaciones ${de} bloqueadas`,
        detalle: "Las bloqueaste en este navegador. Habilitalas desde la configuracion del sitio.",
      };
    case "instalar":
      return {
        titulo: "Agregala a la pantalla de inicio",
        detalle: "En el iPhone los avisos andan solo con la app instalada: Compartir → Agregar a inicio.",
      };
    case "sin-claves":
      return {
        titulo: "Avisos no configurados",
        detalle: "Falta cargar las claves VAPID en el servidor.",
      };
    default:
      return {
        titulo: "Este navegador no manda notificaciones",
        detalle: "Igual los vas a ver en la campana cuando abras la app.",
      };
  }
}

/**
 * El boton de activar/desactivar las notificaciones, listo para pegar.
 *
 * Esta en DOS lugares y tiene que ser el mismo: el menu ☰ del celular y la
 * pantalla de Avisos. Antes estaba solo en el menu ☰, que en una pantalla de
 * escritorio no existe — desde la computadora no habia forma de activarlas.
 */
export function botonAvisosHtml(clase = ""): string {
  return `
    <button data-av-boton class="flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left ${clase}">
      <span class="relative shrink-0">
        ${iconoCampana}
        <span data-av-punto class="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white"></span>
      </span>
      <span class="flex-1">
        <span data-av-titulo class="block font-medium">Notificaciones</span>
        <span data-av-detalle class="block text-xs opacity-80">Revisando...</span>
      </span>
    </button>`;
}

/**
 * Le da vida al boton de botonAvisosHtml() que este dentro de `raiz`.
 *
 * El permiso SOLO se puede pedir dentro de un clic del usuario: por eso es un
 * boton y no algo automatico al abrir la app.
 */
export function engancharBotonAvisos(raiz: ParentNode): void {
  const boton = raiz.querySelector<HTMLButtonElement>("[data-av-boton]");
  if (!boton) return;
  const titulo = boton.querySelector("[data-av-titulo]") as HTMLElement;
  const detalle = boton.querySelector("[data-av-detalle]") as HTMLElement;
  const punto = boton.querySelector("[data-av-punto]") as HTMLElement;
  let estado: EstadoAvisos = "desactivados";
  let colorPuesto = "";

  const pintar = (): void => {
    const t = textoAvisos(estado);
    titulo.textContent = t.titulo;
    detalle.textContent = t.detalle;
    boton.disabled = !["activados", "desactivados"].includes(estado);
    boton.classList.toggle("opacity-60", boton.disabled);

    // Verde = los avisos estan llegando; rojo = no. Se saca el color anterior
    // antes de poner el nuevo, si no se van acumulando en el class.
    const e = estiloAvisos(estado);
    if (colorPuesto) boton.classList.remove(...colorPuesto.split(" "));
    boton.classList.add(...e.caja.split(" "));
    colorPuesto = e.caja;
    punto.className = `absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${e.punto}`;
  };

  void estadoAvisos().then((e) => {
    estado = e;
    pintar();
  });

  boton.addEventListener("click", async () => {
    detalle.textContent = "Un momento...";
    try {
      estado = estado === "activados" ? await desactivarAvisos() : await activarAvisos();
    } catch {
      estado = await estadoAvisos();
    }
    pintar();
  });
}
